import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, X, Utensils, Truck, ChefHat, Cookie, Star } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const API_URL = window.location.origin;

const CATEGORY_CONFIG = {
  catering_plans: {
    label: 'Catering Plans',
    description: 'Meal portion sizes available to clients (e.g. Small Meals, Large Meals)',
    icon: Utensils,
    color: 'green'
  },
  hte_meal_types: {
    label: 'HTE Meal Types',
    description: 'Heat to Eat serving format options',
    icon: ChefHat,
    color: 'orange'
  },
  delivery_options: {
    label: 'Delivery Options',
    description: 'Available delivery time slots for clients',
    icon: Truck,
    color: 'blue'
  },
  basic_addons: {
    label: 'Basic Add-ons',
    description: 'Standard add-on items included with meals (e.g. Fresh Fruit, Yoghurt Tubes)',
    icon: Cookie,
    color: 'teal'
  },
  premium_addons: {
    label: 'Premium Add-ons',
    description: 'Premium add-on items available for Large Meals (e.g. Chocolate Brownie, Carrot Cake)',
    icon: Star,
    color: 'purple'
  }
};

const OptionCategory = ({ categoryKey, options, onAdd, onDelete, deleting }) => {
  const [newValue, setNewValue] = useState('');
  const config = CATEGORY_CONFIG[categoryKey];
  const Icon = config.icon;

  const colorMap = {
    green: { badge: 'bg-green-100 text-green-700', icon: 'bg-green-100 text-green-600', border: 'border-green-200' },
    orange: { badge: 'bg-orange-100 text-orange-700', icon: 'bg-orange-100 text-orange-600', border: 'border-orange-200' },
    blue: { badge: 'bg-blue-100 text-blue-700', icon: 'bg-blue-100 text-blue-600', border: 'border-blue-200' },
    teal: { badge: 'bg-teal-100 text-teal-700', icon: 'bg-teal-100 text-teal-600', border: 'border-teal-200' },
    purple: { badge: 'bg-purple-100 text-purple-700', icon: 'bg-purple-100 text-purple-600', border: 'border-purple-200' }
  };
  const colors = colorMap[config.color];

  const handleAdd = async () => {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    const success = await onAdd(categoryKey, trimmed);
    if (success) setNewValue('');
  };

  return (
    <Card className="border-slate-200/60 shadow-sm">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors.icon}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {config.label}
            </CardTitle>
            <CardDescription>{config.description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current options */}
        <div className="flex flex-wrap gap-2">
          {options.map((option) => (
            <Badge
              key={option}
              className={`${colors.badge} text-sm py-1.5 px-3 flex items-center gap-2`}
              data-testid={`option-badge-${categoryKey}-${option.replace(/[^a-zA-Z0-9]/g, '-')}`}
            >
              {option}
              {options.length > 1 && (
                <button
                  onClick={() => onDelete(categoryKey, option)}
                  disabled={deleting === `${categoryKey}-${option}`}
                  className="hover:opacity-70 transition-opacity ml-1"
                  data-testid={`delete-option-${categoryKey}-${option.replace(/[^a-zA-Z0-9]/g, '-')}`}
                >
                  {deleting === `${categoryKey}-${option}` ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <X className="w-3 h-3" />
                  )}
                </button>
              )}
            </Badge>
          ))}
        </div>

        {/* Add new option */}
        <div className="flex gap-2">
          <Input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder={`Add new ${config.label.toLowerCase().slice(0, -1)}...`}
            className="rounded-xl flex-1"
            data-testid={`add-option-input-${categoryKey}`}
          />
          <Button
            onClick={handleAdd}
            disabled={!newValue.trim()}
            className="rounded-full bg-green-600 hover:bg-green-700 shrink-0"
            data-testid={`add-option-button-${categoryKey}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminSettings = () => {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/options`, { withCredentials: true });
      setOptions(data);
    } catch (e) {
      // Error handled silently
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (category, value) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/settings/options`,
        { category, value },
        { withCredentials: true }
      );
      setOptions(data);
      toast.success(`Added "${value}"`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add option');
      return false;
    }
  };

  const handleDelete = async (category, value) => {
    setDeleting(`${category}-${value}`);
    try {
      const { data } = await axios.delete(`${API_URL}/api/admin/settings/options`, {
        data: { category, value },
        withCredentials: true
      });
      setOptions(data);
      toast.success(`Removed "${value}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove option');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Settings
          </h1>
          <p className="text-slate-500 mt-1">
            Manage profile options available to clients. Changes apply immediately to all forms.
          </p>
        </div>

        {options && Object.keys(CATEGORY_CONFIG).map((key) => (
          <React.Fragment key={key}>
            {key === 'basic_addons' && (
              <div className="pt-4 border-t border-slate-200">
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Add-on Management
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                  Manage the add-on items available in the client order form.
                </p>
              </div>
            )}
            <OptionCategory
              categoryKey={key}
              options={options[key] || []}
              onAdd={handleAdd}
              onDelete={handleDelete}
              deleting={deleting}
            />
          </React.Fragment>
        ))}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
